import React from 'react'
import { PlayerAPI, PlayerEventBase } from 'bitmovin-player'
import { UIInstanceManager } from 'bitmovin-player-ui'
import { EventDispatcher } from 'bitmovin-player-ui/dist/js/framework/eventdispatcher'
import { AudioTrack } from 'bitmovin-player/types/core/PlayerAPI'
import { SubtitleTrack } from 'bitmovin-player/types/subtitles/API'
import { NextRouter } from 'next/router'
import type { AngelPlayerData } from '@/organisms/AngelPlayer/types/AngelPlayerTypes'
import { MomentTypes } from '@/types/codegen-federation'
import { Locale } from '@/utils/LocaleUtil'

export interface BitmovinUILoaderProps {
  markers?: TimelineMarker[]
  disableCommunityView?: boolean
  hideCommunityViewToggleButton?: boolean
  showContentShareButton?: boolean
  showParentalControlsButton?: boolean
  parentalControlsEnabled?: boolean
  parentalControlsApplied?: number
  parentalControlsTotal?: number
  player?: PlayerAPI
  uiLanguage?: Locale
}

export interface BitmovinPlayerProps {
  disableAssetLoad?: boolean
  aspectClassName?: string
  asset: Asset
  autoplay?: boolean
  enableAds?: boolean
  onViewModeChanged?: (event: ViewModeChangedEvent) => void
  onAdStarted?: () => void
  onAdFinished?: () => void
  onAdSkipped?: () => void
  markers?: TimelineMarker[]
  overlays?: PlayerOverlay[]
  onPlayerInit?: (player: PlayerAPI) => void
  anonymousUserId?: string
  loggedInUserId?: string | null | undefined
  sessionId?: string
  isStrict?: boolean
  disableCommunityView?: boolean
  showContentShareButton?: boolean
  showParentalControlsButton?: boolean
  parentalControlsEnabled?: boolean
  parentalControlsApplied?: number
  parentalControlsTotal?: number
  hideCommunityViewToggleButton?: boolean
  isLoginRequired?: boolean
  isMuted?: boolean
  uiLanguage?: Locale
}

export type TimelineMarkerType = 'heart' | string

export interface TimelineMarker {
  time: number
  duration?: number
  title?: string
  imageUrl?: string
  cssClasses?: string[]
  markerType?: TimelineMarkerType
  sceneType?: MomentTypes
}

interface NoArgs {}
export interface CommunityViewChagedArgs extends NoArgs {
  isOn: boolean
  caller?: string
}

export interface AngelUIInstanceManager extends UIInstanceManager {
  onCommunityViewToggled: EventDispatcher<UIInstanceManager, CommunityViewChagedArgs>
  onContentShare: EventDispatcher<UIInstanceManager, NoArgs>
  onParentalControls: EventDispatcher<UIInstanceManager, NoArgs>
}

export interface UIManager {
  addTimelineMarker: (timelineMarker: TimelineMarker) => void
  removeTimelineMarker: (timelineMarker: TimelineMarker) => void
  release: () => void
  showTimelineMarkers: () => void
  hideTimelineMarkers: () => void
  markScene: (timelineMarker: TimelineMarker) => void
  uiInstanceManagers: AngelUIInstanceManager[]
  currentUi: AngelUIInstanceManager
}

export interface BitmovinPlayerClassProps extends BitmovinPlayerProps, AngelPlayerData {
  router: NextRouter
}

export type VideoType = 'episode' | 'video' | 'story' | 'livestream' | 'pif-explainer' | 'torch'

export interface SourceMetadata {
  upNext?: UpNextConfig
  nifty?: BitmovinNft
  moments?: BitmovinMoments
  videoType?: VideoType
}

export interface UpNextConfig {
  /**
   * Required. If this value is missing, the up next functionality will be disabled.
   * Callback used when the user clicks the button or when the automatic go to next is called. Should include a call of next/router.push(nextVideoUrl).
   */
  goToNext?: () => void

  /**
   * Required. If this value is missing, the up next functionality will be disabled.
   * In seconds, should be set to appear before the end of the video. Generally 8 seconds after the credits start.
   */
  showTime?: number
  /**
   * Optional start time of the intro segment in seconds. If provided, will be used to hide the up next prompt during the intro.
   */
  introStartTime?: number | null
  /**
   * Optional end time of the intro segment in seconds. If provided, will be used to hide the up next prompt during the intro.
   */
  introEndTime?: number | null
}

export interface BitmovinNft {
  nftEnabled: boolean
  moments?: BitmovinMoment[]
}

export interface BitmovinMoments {
  enabled: boolean
}

/**
 * HH:MM:SS:FF
 * Frame should be a number between 00 and 23 for 24fps videos
 */
export type SmpteTimestamp = string

const SMPTE_PATTERN = /^(?:(?:[0-1]\d|2[0-3]):)(?:[0-5]\d:){2}(?:[0-6]\d)$/

/** Validates and narrows a raw string to SmpteTimestamp format (HH:MM:SS:FF). */
export function parseSmpte(value: string | undefined): SmpteTimestamp | undefined {
  return value && SMPTE_PATTERN.test(value) ? value : undefined
}

export interface AssetAnalytics {
  airdate?: string
  content_id: string
  description: string
  episodeNumber?: number
  seasonId?: string
  title: string
  total_length?: number
  position: number
  videoType: 'episode' | 'video' | 'story' | 'livestream' | 'pif-explainer' | 'torch'
}

export interface Asset {
  id?: number
  projectSlug?: string
  projectName?: string
  publisherName?: string
  name: string
  position?: number | SmpteTimestamp
  frameRate: number
  framesDroppedEachMinute: number
  adjustmentFactor: number
  vmapUrl?: string
  sourceConfig: {
    guid: string
    slug: string
    hls: string
    poster?: string
    title: string
    subtitle?: string
    description?: string
    credits?: number
    metadata?: SourceMetadata
  }
  analytics: AssetAnalytics
}

export interface BitmovinMoment {
  startFrameNumber: number
  endFrameNumber: number
  title: string
}

export enum PlayerEvent {
  Paused = 'paused',
  Playing = 'playing',
  Play = 'play',
  Seek = 'seek',
  Seeked = 'seeked',
  SourceLoaded = 'sourceloaded',
  TimeChanged = 'timechanged',
  PlaybackFinished = 'playbackfinished',
  AudioChanged = 'audiochanged',
  SubtitleEnabled = 'subtitleenabled',
  SubtitleDisabled = 'subtitledisabled',
  Ready = 'ready',
  ViewModeChanged = 'viewmodechanged',
  AdStarted = 'adstarted',
  AdFinished = 'adfinished',
  AdSkipped = 'adskipped',
}

export interface PlayerOverlay {
  key: string
  Component: React.FC
}

export interface ViewModeChangedEvent extends PlayerEventBase {
  from: ViewMode
  to: ViewMode
  legacy: boolean
}

export interface SmpteController {
  load: (asset: AssetDescription) => Promise<void>
  unload: () => Promise<void>
  seekToSMPTE: (smpteString: string) => void
  getCurrentSmpte: () => string | undefined
  getCurrentTime: () => number
  step: (stepSize: number) => void
  play: () => void
  pause: () => void
  seek: (seekTo: number) => void
  exitFullscreen: () => void
  enterFullscreen: () => void
  isFullscreen: () => boolean
  isPlaying: () => boolean
  destroy: () => void
  getVolume: () => number
  setVolume: (volume: number) => void
  mute: () => void
  unmute: () => void
  isMuted: () => boolean
  getPlaybackSpeed: () => number
  setPlaybackSpeed: (speed: number) => void
  getAvailableAudio: () => AudioTrack[]
  getSubtitleList: () => SubtitleTrack[]
  setAudio: (audioId: string) => void
  enableSubTitle: (subtitleTrackId: string) => void
  disableSubTitle: (subtitleTrackId?: string) => void
}

export enum ViewMode {
  Inline = 'inline',
  Fullscreen = 'fullscreen',
  PictureInPicture = 'pictureinpicture',
}

export interface AssetDescription {
  name: string
  sourceConfig: {
    [keys: string]: unknown
  }
  framesPerSecond: number
  adjustmentFactor: number
  framesDroppedAtFullMinute: number
  frameDuration: number
  offsetToMidFrame: number
  position?: number | SmpteTimestamp
}

export enum AdTagType {
  VMAP = 'vmap',
  VAST = 'vast',
}
